// src/utils/axiosInstance.js

import axios from "axios";

const getBaseUrl = () => {
  // In production/Vercel deployments, check for explicit API URL override
  if (process.env.NODE_ENV === 'production') {
    // If REACT_APP_API_URL is explicitly set, use it
    if (process.env.REACT_APP_API_URL !== undefined) {
      return process.env.REACT_APP_API_URL; // Can be empty string for relative URLs
    }
    // Default to production backend for main deployment
    return 'https://teach-ai-db-backend.vercel.app';
  }
  // Local development
  return 'http://localhost:5000';
};

//API URL
const apiAxiosInstance = axios.create({
  baseURL: getBaseUrl(),
  withCredentials: true,
  timeout: 9000, // Bump default timeout to better accommodate serverless cold starts
  headers: {
    'Content-Type': 'application/json'
  }
});

//AI URL
export const aiAxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_AI_URL || "http://localhost:5001",
  timeout: 60000, // 60 second timeout for AI operations
  headers: {
    'Content-Type': 'application/json'
  }
});

aiAxiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Add a request interceptor
apiAxiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Set appropriate timeout based on request type
    if (config.url?.includes('/detailedstandards')) {
      const isProd = process.env.NODE_ENV === 'production';
      config.timeout = isProd ? 12000 : 18000;
    } else if (config.url?.includes('/auth/verify')) {
      config.timeout = 8000;
    } else if (config.url?.includes('/google-forms/')) {
      config.timeout = 15000;
    } else if (config.url?.includes('/google-classroom/')) {
      config.timeout = 15000;
    } else if (config.url?.includes('/small-group-lesson-plans')) {
      config.timeout = 15000;
    } else if (config.url?.includes('/ai-analysis')) {
      config.timeout = 15000;
    } else if (config.url?.includes('/assessment-passages')) {
      config.timeout = 15000;
    } else if (config.url?.includes('/assignments')) {
      config.timeout = 12000;
    } else if (config.url?.includes('/teaching-chat/')) {
      config.timeout = 10000;
    } else if (config.url?.includes('/auth/')) {
      config.timeout = 15000;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging and error handling
apiAxiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Special handling for signup endpoint
    if (error.config?.url === '/auth/signup') {
      // For timeout errors on signup
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        const customError = new Error('Backend is currently slow. Please try again in a few minutes or use Google login instead.');
        customError.name = 'SignupTimeoutError';
        return Promise.reject(customError);
      }
    }

    // Special handling for verification endpoint
    if (error.config?.url === '/auth/verify') {
      // For timeout errors
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        const timeoutError = new Error('Authentication is taking longer than expected. Please wait...');
        timeoutError.name = 'Error';
        return Promise.reject(timeoutError);
      }
      
      // For server busy errors
      if (error.response?.status === 503) {
        const busyError = new Error('Server busy. Please wait...');
        busyError.name = 'Error';
        return Promise.reject(busyError);
      }
      
      // For auth failures
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        return Promise.reject(new Error('Session expired. Please login again.'));
      }
      
      // For other verify errors, don't clear token
      return Promise.reject(error.response?.data?.message || 'Verification failed');
    }

    // Only redirect to login for non-verification auth failures
    if (error.response?.status === 401 && 
        !error.message?.includes('timeout') && 
        error.config?.url !== '/auth/verify') {
      const failingUrl = error.config?.url || '';
      // Do NOT log the user out for Google Classroom auth (missing scopes/consent)
      if (failingUrl.includes('/api/google-classroom/')) {
        return Promise.reject(error);
      }
      // Do NOT redirect for public routes (they don't require auth)
      if (failingUrl.includes('/api/public-assignments/') || (failingUrl.includes('/api/tests/') && failingUrl.includes('/public'))) {
        return Promise.reject(error);
      }
      // Do NOT redirect for teacher-profile checks (used to check if user is a teacher)
      if (failingUrl.includes('/auth/teacher-profile')) {
        return Promise.reject(error);
      }
      localStorage.removeItem('token');
      window.location.href = '/login';
      return Promise.reject(new Error('Session expired. Please login again.'));
    }

    // Handle canceled/aborted requests (don't treat as errors)
    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.message === 'canceled') {
      return Promise.reject(error);
    }

    // Handle network errors
    if (!error.response) {
      return Promise.reject(new Error('Network error. Please check your connection.'));
    }

    // Handle timeouts specifically
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return Promise.reject(new Error('Request timed out. Please try again.'));
    }

    // Handle 400 errors specifically (like our student ID conflict)
    if (error.response?.status === 400) {
      console.error('[AXIOS] 400 error response:', error.response.data);
      return Promise.reject(error);
    }

    // Handle other errors
    return Promise.reject(error.response?.data?.message || error.message || 'An error occurred');
  }
);

export default apiAxiosInstance;
